import React, { useState } from 'react';
import { Mail, Lock, Building, Globe, CheckCircle, ArrowRight, User, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { brandLogos, brandName } from '../config/brand';
import { partnerLoginUrl } from '../config/partnerPanel';
import { PartnerPanelLink } from './PartnerPanelLink';

export const PartnerRegistration: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    organization: '',
    country: '',
    terms: false
  });
  
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string>('');

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Spain', 'Italy',
    'Netherlands', 'Sweden', 'Norway', 'Denmark', 'Finland', 'Australia', 'Japan',
    'South Korea', 'Singapore', 'India', 'China', 'Brazil', 'Mexico', 'Argentina',
    'Chile', 'Colombia', 'South Africa', 'Nigeria', 'Egypt', 'UAE', 'Saudi Arabia',
    'Turkey', 'Russia', 'Poland', 'Czech Republic', 'Hungary', 'Romania', 'Bulgaria',
    'Croatia', 'Slovenia', 'Slovakia', 'Estonia', 'Latvia', 'Lithuania', 'Other'
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (!formData.fullName) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.country) {
      newErrors.country = 'Country is required';
    }
    
    if (!formData.terms) {
      newErrors.terms = 'You must agree to the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const sendOTP = async () => {
    if (!formData.email) {
      setErrors({ email: 'Email is required' });
      return;
    }
    
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setOtpSent(true);
      setErrors({});
    } catch (error) {
      setErrors({ email: 'Failed to send OTP. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otpCode) {
      setErrors({ otp: 'OTP code is required' });
      return;
    }
    
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsVerified(true);
      setErrors({});
    } catch (error) {
      setErrors({ otp: 'Invalid OTP code. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !isVerified) {
      return;
    }
    
    setIsLoading(true);
    setApiError('');
    
    try {
      const response = await apiService.registerPartner({
        email: formData.email,
        password: formData.password,
        fullName: formData.fullName,
        organization: formData.organization,
        phone: '', // Add phone field if needed
        country: formData.country
      });

      if (response.status?.success) {
        // Registration successful
        console.log('Registration successful:', response.data);
        alert('Registration successful! You will receive Partner Panel access via email.');
        // Redirect to login page
        navigate('/partner-login');
      } else {
        // Registration failed
        setApiError(response.status?.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setApiError('Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="io-site partner-auth-page partner-registration">
      <div className="registration-layout">
        {/* Left Side - Banner Content */}
        <div className="banner-content">
          <div className="banner-text">
            <div className="logo-container">
              <img
                src={brandLogos.markDark}
                alt={`${brandName} logo`}
                className="partner-logo"
              />
              <div className="logo-divider"></div>
            </div>
            <h1>Let's Build Something Great Together</h1>
            <p>Transform your business with our comprehensive IoT platform. Get access to our Partner Panel and start building smart solutions today.</p>
            <div className="banner-features">
              <div className="feature-item">
                <div className="feature-icon">🚀</div>
                <span>White-Label Solutions</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">🔧</div>
                <span>Technical Support</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">📈</div>
                <span>Growth Opportunities</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Registration Form */}
        <div className="registration-container">
          <div className="registration-header">
            <h2>Partner Registration</h2>
            <p>Fill out the form below to get started</p>
          </div>

        <form onSubmit={handleSubmit} className="registration-form">
          {/* API Error Display */}
          {apiError && (
            <div className={`api-error ${apiError.includes('already exists') ? 'warning' : ''}`}>
              <span>{apiError}</span>
            </div>
          )}

          {/* Email Field */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              <Mail size={18} />
              Email Address *
            </label>
            <div className="email-input-group">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="Enter your business email"
                disabled={isVerified}
              />
              {!isVerified && (
                <button
                  type="button"
                  onClick={sendOTP}
                  disabled={isLoading || !formData.email}
                  className="otp-button"
                >
                  {isLoading ? 'Sending...' : 'Send OTP'}
                </button>
              )}
            </div>
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          {/* OTP Verification */}
          {otpSent && !isVerified && (
            <div className="form-group">
              <label htmlFor="otp" className="form-label">
                <CheckCircle size={18} />
                Verify Email with OTP *
              </label>
              <div className="otp-input-group">
                <input
                  type="text"
                  id="otp"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  className={`form-input ${errors.otp ? 'error' : ''}`}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                />
                <button
                  type="button"
                  onClick={verifyOTP}
                  disabled={isLoading || !otpCode}
                  className="verify-button"
                >
                  {isLoading ? 'Verifying...' : 'Verify'}
                </button>
              </div>
              {errors.otp && <span className="error-message">{errors.otp}</span>}
            </div>
          )}

          {/* Verified Email */}
          {isVerified && (
            <div className="verified-email">
              <CheckCircle size={20} className="verified-icon" />
              <span>Email verified successfully!</span>
            </div>
          )}

          {/* Full Name Field */}
          <div className="form-group">
            <label htmlFor="fullName" className="form-label">
              <User size={18} />
              Full Name *
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              className={`form-input ${errors.fullName ? 'error' : ''}`}
              placeholder="Enter your full name"
            />
            {errors.fullName && <span className="error-message">{errors.fullName}</span>}
          </div>

          {/* Password Field */}
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              <Lock size={18} />
              Password *
            </label>
            <div className="password-input-group">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="Create a strong password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          {/* Organization Field */}
          <div className="form-group">
            <label htmlFor="organization" className="form-label">
              <Building size={18} />
              Organization Name
            </label>
            <input
              type="text"
              id="organization"
              name="organization"
              value={formData.organization}
              onChange={handleInputChange}
              className={`form-input ${errors.organization ? 'error' : ''}`}
              placeholder="Enter your company name (optional)"
            />
            {errors.organization && <span className="error-message">{errors.organization}</span>}
          </div>

          {/* Country Field */}
          <div className="form-group">
            <label htmlFor="country" className="form-label">
              <Globe size={18} />
              Country/Region *
            </label>
            <select
              id="country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              className={`form-select ${errors.country ? 'error' : ''}`}
            >
              <option value="">Select your country</option>
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
            {errors.country && <span className="error-message">{errors.country}</span>}
          </div>

          {/* Terms and Conditions */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="terms"
                checked={formData.terms}
                onChange={handleInputChange}
                className="checkbox-input"
              />
              <span className="checkbox-custom"></span>
              <span className="checkbox-text">
                I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms & Conditions</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> *
              </span>
            </label>
            {errors.terms && <span className="error-message">{errors.terms}</span>}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !isVerified}
            className="submit-button"
          >
            {isLoading ? (
              'Creating Account...'
            ) : (
              <>
                <span>Register as Partner</span>
                <ArrowRight size={20} />
              </>
            )}
          </button>
        </form>

          <div className="registration-footer">
            <p>Already have a partner account? <PartnerPanelLink to={partnerLoginUrl()}>Sign in here</PartnerPanelLink></p>
          </div>
        </div>
      </div>
    </div>
  );
};
